import {
  DIFF_STYLE,
  DIFF_LEGEND_ORDER,
  type ChangeClass,
  type DiffResult,
  type DiffIdentity,
} from '@drawing2d/core';
import { t, type Lang } from '../i18n.js';

/**
 * The numbers, and what they are numbers of.
 *
 * Three things here are deliberately not simplified.
 *
 * **The by-page tables are given twice.** The same integer names a different
 * sheet in the two versions the moment a sheet is inserted or removed, so one
 * merged table would file a change under a page number the reviewer cannot find
 * it on. Two tables, each labelled with the version it belongs to.
 *
 * **Ambiguous is a count of marks, and says so.** It is not a rate, not a
 * quality score and not a share of anything, and a reviewer who reads it as one
 * would conclude the wrong thing about how much of the drawing is undecided.
 *
 * **The tolerance is shown with its unit and the profile version beside it.**
 * At unknown scale there is no millimetre, and a bare number would be read as
 * one. The profile version is there because a tolerance without the tuning that
 * produced it is not reproducible.
 */

export function ChangeSummary({
  diff,
  identity,
  lang,
}: {
  diff: DiffResult;
  identity: DiffIdentity;
  lang: Lang;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);
  const unit = diff.displacementUnit === 'mm' ? 'mm' : s('unitPageDiagonal');

  return (
    <section className="panel change-summary">
      <h2>{s('summaryTitle')}</h2>

      <h3>{s('byClassTitle')}</h3>
      <ul className="counts by-class">
        {DIFF_LEGEND_ORDER.map((c) => (
          <li key={c} data-change-class={c}>
            <span>{DIFF_STYLE[c].label[lang]}</span>
            <b>{diff.summary.byClass[c]}</b>
          </li>
        ))}
      </ul>

      <h3>{s('byBasePageTitle')}</h3>
      <PageTable table={diff.summary.byBasePage} which="base" lang={lang} />

      <h3>{s('byComparePageTitle')}</h3>
      <PageTable table={diff.summary.byComparePage} which="compare" lang={lang} />
      <p className="note">{s('byPageNote')}</p>

      <ul className="counts facts">
        <li data-fact="ambiguous">
          <span>{s('ambiguousMarks')}</span>
          <b>{diff.summary.ambiguous}</b>
        </li>
        <li data-fact="coverage">
          <span>{s('coverage')}</span>
          <b>{(diff.summary.coverage * 100).toFixed(1)}%</b>
        </li>
        <li data-fact="tolerance">
          <span>{s('toleranceLabel')}</span>
          <b>
            {diff.tolerance.toFixed(4)} {unit}
          </b>
        </li>
        <li data-fact="matcher-profile">
          <span>{s('matcherProfile')}</span>
          <b>{diff.matcherProfileVersion}</b>
        </li>
        <li data-fact="comparison-space">
          <span>{s('comparisonSpaceLabel')}</span>
          <b>{diff.comparisonSpace === 'drawing_mm' ? s('spaceDrawingMm') : s('spaceNormalised')}</b>
        </li>
        <li data-fact="entity-counts">
          <span>{s('entityCounts')}</span>
          <b>
            {diff.baseEntityCount} → {diff.compareEntityCount}
          </b>
        </li>
        <li data-fact="sheet-counts">
          <span>{s('sheetCounts')}</span>
          <b>
            {diff.basePageCount} → {diff.comparePageCount}
          </b>
        </li>
      </ul>
      <p className="note">{s('ambiguousNote')}</p>

      <h3>{s('alignmentTitle')}</h3>
      <ul className="counts alignment">
        {diff.pageAlignment.map((pair, index) => (
          <li key={index} data-sheet={index}>
            <span>
              {s('sheet')} {index + 1} · {describeSheet(pair.basePageIndex, pair.comparePageIndex, lang)}
            </span>
            <b>
              {s('similarity')} {pair.similarity.toFixed(2)} · {s('margin')}{' '}
              {pair.margin.toFixed(2)}
            </b>
          </li>
        ))}
      </ul>
      {diff.pageAlignment.some((p) => p.margin === 0 && p.comparePageIndex !== null && p.basePageIndex !== null) && (
        <p className="note">{s('marginZeroNote')}</p>
      )}
      {identity.extractorBundleMismatch && <p className="finding">{s('bundleMismatch')}</p>}
    </section>
  );
}

function PageTable({
  table,
  which,
  lang,
}: {
  table: Record<number, Partial<Record<ChangeClass, number>>>;
  which: 'base' | 'compare';
  lang: Lang;
}): JSX.Element {
  const rows = Object.entries(table).sort((a, b) => Number(a[0]) - Number(b[0]));
  if (rows.length === 0) {
    return <p className="note">{t('notInThisVersion', lang)}</p>;
  }
  return (
    <ul className={`counts by-page by-${which}-page`}>
      {rows.map(([pageIndex, byClass]) => (
        <li key={pageIndex} data-page-index={pageIndex}>
          <span>
            {t('pageLabel', lang)} {Number(pageIndex) + 1}
          </span>
          <b>
            {DIFF_LEGEND_ORDER.filter((c) => (byClass[c] ?? 0) > 0)
              .map((c) => `${byClass[c] ?? 0} ${DIFF_STYLE[c].label[lang]}`)
              .join(' · ')}
          </b>
        </li>
      ))}
    </ul>
  );
}

export function describeSheet(
  basePageIndex: number | null,
  comparePageIndex: number | null,
  lang: Lang,
): string {
  if (basePageIndex === null) {
    return t('sheetNew', lang).replace('{m}', String((comparePageIndex ?? 0) + 1));
  }
  if (comparePageIndex === null) {
    return t('sheetGone', lang).replace('{n}', String(basePageIndex + 1));
  }
  return t('sheetPair', lang)
    .replace('{n}', String(basePageIndex + 1))
    .replace('{m}', String(comparePageIndex + 1));
}
